import type { PriceOverrideSort, PriceOverrideStatus, SortOrder } from "@classes-service-types/index";
import { PriceOverrideStatus as PriceOverrideStatusOptions } from "@classes-service-types/index";
import { isEqual as dateEqual, format as formatDate, isValid } from "date-fns";
import { isEqual } from "lodash";
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ALL_PRICE_OVERRIDE_STATUSES_SORTED } from "@/components/OperationsApp/components/PriceOverrideHeader/constants.ts";
import { useOverrideReasons } from "@/components/OperationsApp/composables/useGetOverrideReasons.ts";
import { PRICE_OVERRIDE_API_DATE_FORMAT } from "@/constants.ts";

export const useProductTypeReasonFilters = () => {
  const route = useRoute();
  const router = useRouter();

  const libraryId = ref<Array<string>>([]);
  const reasonId = ref<Array<string>>([]);

  watch(
    () => route.query,
    async (newParams, oldParams) => {
      if (newParams !== oldParams) {
        let newLibraryId = newParams.libraryId ? newParams.libraryId : [];
        newLibraryId = Array.isArray(newLibraryId) ? newLibraryId : [newLibraryId];
        if (!isEqual(newLibraryId.sort(), libraryId.value.sort())) {
          libraryId.value = newLibraryId.map((value) => `${value}`).filter((val) => !!val);
        }

        let newReasonId = newParams.reasonId ? newParams.reasonId : [];
        newReasonId = Array.isArray(newReasonId) ? newReasonId : [newReasonId];
        if (!isEqual(newReasonId.sort(), reasonId.value.sort())) {
          reasonId.value = newReasonId.map((value) => `${value}`).filter((val) => !!val);
        }
      }
    },
    { immediate: true },
  );

  const { data: overrideReasonsData, isLoading: overrideReasonsIsLoading } = useOverrideReasons();

  const updateLibraryId = async (updatedLibraryId: Array<string>) => {
    libraryId.value = updatedLibraryId;
    await router.push({
      query: {
        ...route.query,
        libraryId: updatedLibraryId,
      },
    });
  };

  const updateReasonId = async (updatedReasonId: Array<string>) => {
    reasonId.value = updatedReasonId;
    await router.push({
      query: {
        ...route.query,
        reasonId: updatedReasonId,
      },
    });
  };

  const allowedReasonIds = computed(() => {
    let allowedReasonIds = new Set<string>();
    for (const selectedLibraryId of libraryId.value) {
      const foundReasonIds = overrideReasonsData.value?.libraryToReasonIdMap.get(selectedLibraryId);
      allowedReasonIds = foundReasonIds ? new Set([...allowedReasonIds, ...foundReasonIds]) : allowedReasonIds;
    }
    return allowedReasonIds;
  });

  const isReasonEnabled = (selectedReasonId: string) => {
    return (
      libraryId.value?.length === 0 ||
      reasonId.value.includes(selectedReasonId) ||
      allowedReasonIds.value?.has(selectedReasonId)
    );
  };

  const allowedLibraryIds = computed(() => {
    let allowedLibraryIds = new Set<string>();
    for (const selectedReasonId of reasonId.value) {
      const foundLibraryIds = overrideReasonsData.value?.reasonToLibraryIdMap.get(selectedReasonId);
      allowedLibraryIds = foundLibraryIds ? new Set([...allowedLibraryIds, ...foundLibraryIds]) : allowedLibraryIds;
    }
    return allowedLibraryIds;
  });

  const isProductTypeEnabled = (selectedProductTypeId: string) => {
    return (
      reasonId.value?.length === 0 ||
      libraryId.value.includes(selectedProductTypeId) ||
      allowedLibraryIds.value?.has(selectedProductTypeId)
    );
  };

  return {
    libraryId,
    updateLibraryId,
    reasonId,
    updateReasonId,
    overrideReasonsData,
    overrideReasonsIsLoading,
    isProductTypeEnabled,
    isReasonEnabled,
  };
};
